<template>
	<v-dialog
		v-model="open"
		persistent
		max-width="700"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<!-- @click:outside="$emit('close')" -->
		<v-card :tile="$vuetify.breakpoint.xs">
			<v-toolbar dark color="primary" dense flat>
				<v-toolbar-title style="color: white">セルの入力データ設定</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon dark @click="$emit('close')" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-form v-model="valid" class="pa-2" ref="form">
				<v-row v-if="existingCell" no-gutters justify="end">
					<v-btn color="secondary" @click="deleteCell">セルの内容を削除する</v-btn>
				</v-row>
				<v-select
					v-if="modelNames.length"
					v-model="excelCell.base_model"
					label="参照データ"
					:items="modelNames"
					class="mt-4"
					item-text="name"
					item-value="id"
					:persistent-hint="!excelCell.base_model"
					hint="選択しなければ台帳の参照データの項目から項目名を選択できます。"
					clearable
					dense
					outlined
					@change="setColumnNames"
				/>
				<v-select
					v-model="excelCell.column_name"
					label="項目名(必須)"
					:items="columnNameOptions"
					class="mt-4"
					item-text="name"
					item-value="id"
					:rules="[rules.required]"
					required
					dense
					outlined
					@change="changeColumnName"
				/>

				<h6 class="text-body-2 font-weight-bold">計算式を設定</h6>
				<v-radio-group
					class="mt-2"
					v-model="showSection"
					row
					@change="changeSection"
					hide-details
					dense
				>
					<v-radio label="使用しない" :value="null" />
					<v-radio
						v-for="option in showSectionOptions"
						:key="option.value"
						:label="option.label"
						:value="option.value"
					/>
				</v-radio-group>
				<div v-if="showSection == 'prefix'" class="mt-4">
					<h6 class="text-body-2 font-weight-bold">計算式</h6>
					<div class="my-2">
						<v-chip>
							{{ excelCell.prefix_number ? excelCell.prefix_number : "数値を入力してください" }}
						</v-chip>
						<v-chip>
							{{ formulaSymbol(excelCell.prefix_formula) }}
						</v-chip>
						<v-chip>
							{{ excelCell.column_name ? columnName : "項目名を選択してください" }}
						</v-chip>
					</div>
					<v-text-field
						v-model="excelCell.prefix_number"
						label="数値(必須)"
						type="number"
						hide-spin-buttons
						class="mt-4"
						:rules="showSection == 'prefix' ? [rules.required, rules.floatWithNegative] : []"
						dense
						outlined
					/>
					<v-select
						v-model="excelCell.prefix_formula"
						label="計算式(必須)"
						:items="cellOptions.formulas"
						item-text="name"
						item-value="id"
						class="mt-4"
						:rules="showSection == 'prefix' ? [rules.required] : []"
						dense
						outlined
					/>
				</div>
				<div v-else-if="showSection == 'suffix'" class="mt-4">
					<h6 class="text-body-2 font-weight-bold">計算式</h6>
					<div class="mt-2">
						<v-chip>
							{{ excelCell.column_name ? columnName : "項目名を選択してください" }}
						</v-chip>
						<v-chip>
							{{ formulaSymbol(excelCell.suffix_formula) }}
						</v-chip>
						<v-chip>
							{{ excelCell.suffix_number ? excelCell.suffix_number : "数値を入力してください" }}
						</v-chip>
					</div>
					<v-text-field
						v-model="excelCell.suffix_number"
						label="数値(必須)"
						type="text"
						class="mt-4"
						:rules="showSection == 'suffix' ? [rules.required, rules.floatWithNegative] : []"
						dense
						outlined
					/>
					<v-select
						v-model="excelCell.suffix_formula"
						label="計算式(必須)"
						:items="cellOptions.formulas"
						item-text="name"
						item-value="id"
						class="mt-4"
						:rules="showSection == 'suffix' ? [rules.required] : []"
						dense
						outlined
					/>
				</div>

				<div v-if="associationHasMany" class="mt-4">
					<h6 class="text-body-1 font-weight-bold">
						データが複数存在する可能性があるため、以下の項目も入力してください。
					</h6>
					<div class="mt-2">
						<label class="text-body-2 font-weight-bold"> データを並べる方向(必須) </label>
						<v-radio-group v-model="excelCell.array_direction" class="mt-1" row :rules="associationHasMany ? [rules.required] : []">
							<v-radio label="横方向" key="x" value="x_axis" class="mr-2" dense />
							<v-radio label="縦方向" key="y" value="y_axis" class="mr-2" dense />
						</v-radio-group>
					</div>
					<v-text-field
						v-model="excelCell.max_count"
						label="並べるデータの最大数"
						type="number"
						hide-spin-buttons
						class="mt-4"
						:rules="[rules.integer]"
						dense
						hint="入力しない場合は、デフォルト値の10で登録されます。"
						persistent-hint
						outlined
					/>
					<v-select
						v-model="excelCell.sort_key"
						label="並び替えの元となる項目"
						:items="columnNameOptions"
						class="mt-4"
						item-text="name"
						item-value="id"
						dense
						outlined
					/>
					<div class="mt-4">
						<label class="text-body-2 font-weight-bold"> データを並べる順番 </label>
						<v-radio-group v-model="excelCell.sort_value" class="mt-1" row>
							<v-radio label="昇順" key="asc" value="asc" class="mr-2" dense />
							<v-radio label="降順" key="desc" value="desc" class="mr-2" dense />
						</v-radio-group>
					</div>
				</div>

			</v-form>
			<v-card-actions>
				<v-row class="mt-2" no-gutters>
					<v-col cols="4" />
					<v-col cols="4">
						<v-row no-gutters justify="center">
							<v-btn color="secondary" v-if="existingCell" @click="updateCell" :disabled="!valid"
								>セルを設定する</v-btn
							>
							<v-btn color="secondary" v-else @click="createCell" :disabled="!valid"
								>セルを設定する</v-btn
							>
						</v-row>
					</v-col>
					<v-col cols="4">
						<v-row no-gutters justify="end">
							<v-btn color="grey darken-1" text @click="$emit('close')"> 閉じる </v-btn>
						</v-row>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import _ from "lodash"
import rules from "../../../helper/rules"

export default {
	props: {
		cell: {
			type: Object,
			required: false,
			default: () => {},
		},
		existingCell: {
			type: Object || null,
			required: false,
		},
		coordinate: {
			type: Object,
			required: false,
			default: () => ({
				xCoordinate: {
					type: Number,
					required: true,
					default: null,
				},
				yCoordinate: {
					type: Number,
					required: true,
					default: null,
				},
			}),
		},
		sheetId: {
			type: Number,
			required: true,
			default: null,
		},
		cellOptions: {
			type: Object,
			required: true,
			default: () => ({
				formulas: {
					type: Array,
					required: true,
					default: () => [
						{
							id: {
								type: null,
								required: true,
							},
							name: {
								type: null,
								required: true,
							},
						},
					],
				},
				special_types: {
					type: Array,
					required: true,
					default: () => [
						{
							id: {
								type: null,
								required: true,
							},
							name: {
								type: null,
								required: true,
							},
						},
					],
				},
			}),
			//
		},
		originalModelColumns: {
			type: Array,
			required: true,
			default: () => [
				{
					id: {
						type: null,
						required: true,
					},
					name: {
						type: null,
						required: true,
					},
				},
			],
		},
		associationNames: {
			type: Array,
			required: true,
			default: () => [
				{
					id: {
						type: null,
						required: true,
					},
					name: {
						type: null,
						required: true,
					},
				},
			],
		},
	},
	data() {
		return {
			open: true,
			valid: false,
			rules: rules,
			excelCell: {
				sheet_number: null,
				base_model: null,
				column_name: null,
				column_type: null,
				meta_tag_id: null,
				prefix_formula: null,
				prefix_number: null,
				suffix_formula: null,
				suffix_number: null,
				special_type: null,
				special_string: null,
				x_coordinate: null,
				y_coordinate: null,
				array_direction: null,
				max_count: null,
				sort_key: null,
				sort_value: null,
			},
			selectedModel: null,
			showSection: null,

			showSectionOptions: [
				{ label: "計算(前)", value: "prefix" },
				{ label: "計算(後ろ)", value: "suffix" },
				// { label: "特殊タイプ", value: "special" },
			],
		}
	},
	mounted() {
		if (this.existingCell != null) {
			this.excelCell = _.cloneDeep(this.existingCell)
			this.selectedModel = this.associationNames.find(
				(model) => this.excelCell.base_model == model.id
			)
			if (this.excelCell.suffix_formula && this.excelCell.suffix_number) {
				this.showSection = "suffix"
			} else if (this.excelCell.prefix_formula && this.excelCell.prefix_number) {
				this.showSection = "prefix"
			} else if (this.excelCell.special_type && this.excelCell.special_string) {
				this.showSection = "special"
			}
		}
	},
	methods: {
		setColumnNames() {
			this.excelCell.meta_tag_id = null
			this.excelCell.column_type = null
			this.excelCell.column_name = null
			this.excelCell.array_direction = null
			this.excelCell.max_count = null
			this.excelCell.sort_key = null
			this.excelCell.sort_value = null
			this.selectedModel = this.associationNames.find(
				(model) => this.excelCell.base_model == model.id
			)
		},
		changeColumnName() {
			this.excelCell.meta_tag_id = null
			this.excelCell.column_type = null
			const selectedColumnName = this.columnNameOptions.find(
				(c) => c.id == this.excelCell.column_name
			)
			if (selectedColumnName.meta_tag) {
				this.excelCell.meta_tag_id = Number(selectedColumnName.id)
			}
			this.excelCell.column_type = selectedColumnName.type
		},
		changeSection() {
			if (this.showSection == "prefix") {
				this.excelCell.suffix_formula = null
				this.excelCell.suffix_number = null
				this.excelCell.special_type = null
				this.excelCell.special_string = null
			} else if (this.showSection == "suffix") {
				this.excelCell.prefix_formula = null
				this.excelCell.prefix_number = null
				this.excelCell.special_type = null
				this.excelCell.special_string = null
			} else if (this.showSection == "special") {
				this.excelCell.prefix_formula = null
				this.excelCell.prefix_number = null
				this.excelCell.suffix_formula = null
				this.excelCell.suffix_number = null
			} else {
				this.excelCell.prefix_formula = null
				this.excelCell.prefix_number = null
				this.excelCell.suffix_formula = null
				this.excelCell.suffix_number = null
				this.excelCell.special_type = null
				this.excelCell.special_string = null
			}
		},
		createCell() {
			this.excelCell.sheet_number = this.sheetId
			this.excelCell.x_coordinate = this.coordinate.xCoordinate
			this.excelCell.y_coordinate = this.coordinate.yCoordinate
			this.$emit("add-cell", this.excelCell)
		},
		updateCell() {
			this.$emit("update-cell", this.excelCell)
		},
		deleteCell() {
			this.$emit("delete-cell", this.excelCell)
		},
		// fetchCoordinate(address) {
		// 	const alphabet = address.match(/^[a-zA-Z]+$/)
		// 	const xCoordinate = address.match(/^[0-9]+$/)
		// 	const alphabetCount = 26
		// 	let index = 0
		// 	let length = alphabet.length - 1
		// 	for (i = 0; i <= alphabet.length; i++) {
		// 		if (i < alphabet.length) {
		// 			this.alphabetToNumber(alphabet[i])
		// 			index += (alphabetCount * this.alphabetToNumber(alphabet[i])) ** length
		// 			length -= 1
		// 		} else {
		// 			index += this.alphabetToNumber(alphabet[i])
		// 		}
		// 	}
		// 	return { yCoordinate: index, xCoordinate }
		// },
		// alphabetToNumber(alphabet) {
		// 	const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
		// 	return alphabets.indexOf(alphabet) + 1
		// },
	},
	computed: {
		modelNames() {
			return this.associationNames
			// return this.associationNames.filter((model) => model.association_type != "has_many")
		},
		associationHasMany() {
			if (this.selectedModel && this.selectedModel.association_type == "has_many") {
				return true
			}
			return false
		},
		columnNameOptions() {
			if (this.selectedModel) {
				return this.selectedModel.columns
			} else {
				return this.originalModelColumns
			}
		},
		formulaSymbol() {
			return (formula) => {
				if (!formula) return "計算式を選択してください"
				const selectedFormula = this.cellOptions.formulas.find((f) => f.id == formula)
				if (selectedFormula) {
					return selectedFormula.name
				} else {
					return null
				}
			}
		},
		columnName() {
			const selectedColumnName = this.columnNameOptions.find(
				(c) => c.id == this.excelCell.column_name
			)
			if (!selectedColumnName) return null
			const type = selectedColumnName.type
			if ((type == "integer" || type == "float") && !selectedColumnName.options.length) {
				return selectedColumnName.name
			} else {
				return "項目は数値ではない可能性があります。"
			}
		},
	},
	watch: {
		cell(newValue, _) {
			this.cell = newValue
		},
		coordinate(newValue, _) {
			this.coordinate = newValue
		},
		deep: true,
	},
}
</script>
